import MicrophoneIcon from "assets/icons/Microphone.svg";
import classNames from "classnames";
import { AudioPlayer } from "components/AudioPlayer/AudioPlayer";
import { AudioRecorder } from "components/AudioRecorder/AudioRecorder";
import { useEffect, useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { storage } from "services/storage";

const AudioTesting = ({ onSuccess = () => {} }) => {
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [audioSrc, setAudioSrc] = useState(null);
  const [recorderStatus, setRecorderStatus] = useState(null);

  const disableDeviceSelector = recorderStatus === "recording" || audioSrc;

  const saveAudioDevice = (deviceId) => {
    if (deviceId) {
      storage.set.audioDevice(deviceId);
      setSelectedDevice(deviceId);
    }
  };

  const audioBlobToUrl = (blob) => {
    const audioUrl = URL.createObjectURL(blob);
    setAudioSrc(audioUrl);
  };

  const retest = () => {
    setAudioSrc(null);
  };

  useEffect(() => {
    const handleDevices = (mediaDevices) => {
      const audioSources = mediaDevices.filter(
        ({ kind }) => kind === "audioinput"
      );
      const defaultSystemSource = audioSources.find(
        ({ id }) => id === "default"
      );
      setAvailableDevices(
        audioSources.filter(({ deviceId }) => deviceId !== "default")
      );

      if (audioSources.length && !selectedDevice) {
        if (!defaultSystemSource) {
          const [firstSource] = audioSources;
          saveAudioDevice(firstSource.deviceId);
        } else {
          saveAudioDevice("default");
        }
      }
    };

    if (!navigator.mediaDevices?.enumerateDevices) {
      console.log("enumerateDevices() not supported.");
    } else {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }
  }, []);

  return (
    <div className="absolute top-1/2 left-1/2 w-5/6 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white px-6 py-8 lg:w-2/5">
      <div className="flex items-center gap-1">
        <img src={MicrophoneIcon} alt="mic" className="mt-1" />
        <h2 className="text-lg font-medium text-[#262420] lg:text-3xl">
          Check your microphone settings
        </h2>
      </div>

      <div className="mt-10">
        <h4 className="text-sm text-[#262420] lg:text-base">
          Select Microphone
        </h4>
        <div className="relative mt-2 flex w-full items-center justify-center">
          <select
            className={classNames(
              "w-full appearance-none rounded-full bg-[#0000000F] p-4 text-base outline-none lg:px-[30px] lg:py-5",
              {
                "cursor-not-allowed opacity-50": disableDeviceSelector,
              }
            )}
            value={selectedDevice}
            onChange={({ target }) => saveAudioDevice(target.value)}
            disabled={disableDeviceSelector}
          >
            <option disabled value="">
              Select Device
            </option>
            {availableDevices.map((device) => (
              <option value={device.deviceId} key={device.deviceId}>
                {device.label}
              </option>
            ))}
          </select>
          <FiChevronDown
            className="top-50% pointer-events-none absolute right-5"
            size={24}
          />
        </div>

        {audioSrc ? (
          <div className="mx-auto mt-12 w-full lg:w-1/2">
            <AudioPlayer src={audioSrc} />
          </div>
        ) : (
          <AudioRecorder
            hideTimer
            audioDeviceId={selectedDevice}
            getBlobData={audioBlobToUrl}
            onStatusChange={setRecorderStatus}
            onSubmit={onSuccess}
          />
        )}
      </div>

      {audioSrc && (
        <div className="mt-12">
          <p>Were you able to hear the recorded audio?</p>
          <div className="mt-3 flex flex-col items-center gap-2 lg:flex-row">
            <button
              className="w-full rounded-full bg-cocoa-brown px-0 py-2 text-base font-normal text-white lg:w-auto lg:px-8 lg:text-lg"
              onClick={onSuccess}
            >
              Yes
            </button>
            <button
              className="w-full rounded-full border border-buddha-gold bg-transparent px-0 py-2 text-base font-normal lg:w-auto lg:px-8 lg:text-lg"
              onClick={retest}
            >
              No, try another microphone
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AudioTestingModal = ({ visible = false, ...props }) => {
  return (
    visible && (
      <div className="absolute top-0 left-0 z-10 h-full w-full bg-[#0001] before:absolute before:top-0 before:left-0 before:h-full before:w-full before:backdrop-blur-md">
        <AudioTesting {...props} />
      </div>
    )
  );
};
